import StrapiService from '@/services/StrapiService.js'
import { defineAsyncComponent, markRaw } from 'vue'
import { Capacitor } from '@capacitor/core'
import NotificationsService from '@/services/NotificationsService.js'
import SubService from '@/services/SubService.js'
import ConfigService from '@/services/ConfigService.js'
import AppService from '@/services/AppService.js'

const components = {
  // Question components:
  'question': defineAsyncComponent(() => import('@/modules/core/onboarding/layouts/Question.vue')),
  'risking': defineAsyncComponent(() => import('@/modules/core/onboarding/pages/Risking.vue')),
  'boolean': defineAsyncComponent(() => import('@/modules/core/onboarding/layouts/QuestionBoolean.vue')),
  'cards': defineAsyncComponent(() => import('@/modules/core/onboarding/layouts/QuestionCards.vue')),
  'scale': defineAsyncComponent(() => import('@/modules/core/onboarding/layouts/QuestionScale.vue')),
  'teaser-progress': defineAsyncComponent(() => import('@/modules/core/onboarding/pages/TeaserProgress.vue')),
  'teaser-before-after': defineAsyncComponent(() => import('@/modules/core/onboarding/pages/TeaserBeforeAfter.vue')),
  'teaser-inside-pro': defineAsyncComponent(() => import('@/modules/core/onboarding/pages/TeaserInsidePro.vue')),
  'teaser-inside-pro-safe': defineAsyncComponent(() => import('@/modules/core/onboarding/pages/TeaserInsideProSafe.vue')),
  'proof-promise': defineAsyncComponent(() => import('@/modules/core/onboarding/pages/Promise.vue')),
  'proof-science': defineAsyncComponent(() => import('@/modules/core/onboarding/pages/Science.vue')),
  'proof-microlearning': defineAsyncComponent(() => import('@/modules/core/onboarding/pages/Microlearning.vue')),
  'proof-tools': defineAsyncComponent(() => import('@/modules/core/onboarding/pages/Tools.vue')),
  'simulator': defineAsyncComponent(() => import('@/modules/core/onboarding/pages/Simulator.vue')),
  'financial-situation-result': defineAsyncComponent(() => import('@/modules/core/onboarding/pages/FinancialSituationResult.vue')),
  'achieve-result': defineAsyncComponent(() => import('@/modules/core/onboarding/pages/AchieveResult.vue')),
  // Page components:
  'spin': defineAsyncComponent(() => import('@/modules/core/onboarding/pages/Spin.vue')),
  'intro': defineAsyncComponent(() => import('@/modules/core/onboarding/pages/Intro.vue')),
  'question-group': defineAsyncComponent(() => import('@/modules/core/onboarding/layouts/QuestionsGroup.vue')),
  'question-1-results': defineAsyncComponent(() => import('@/modules/core/onboarding/pages/Result.vue')),
  'proof': defineAsyncComponent(() => import('@/modules/core/onboarding/pages/Proof.vue')),
  'promises': defineAsyncComponent(() => import('@/modules/core/onboarding/layouts/SlidesGroup.vue')),
  'testimonials': defineAsyncComponent(() => import('@/modules/core/onboarding/pages/Testimonials.vue')),
  'crafting': defineAsyncComponent(() => import('@/modules/core/onboarding/pages/Crafting.vue')),
  'crafted': defineAsyncComponent(() => import('@/modules/core/onboarding/pages/Crafted.vue')),
  'consistency-result': defineAsyncComponent(() => import('@/modules/core/onboarding/pages/ConsistencyResult.vue')),
  'notifications': defineAsyncComponent(() => import('@/modules/core/onboarding/pages/Notifications.vue')),
  'completed': defineAsyncComponent(() => import('@/modules/core/onboarding/pages/Completed.vue')),
  'choose': defineAsyncComponent(() => import('@/modules/core/onboarding/pages/Choose.vue')),
  'propup': defineAsyncComponent(() => import('@/modules/core/onboarding/pages/Propup.vue')),
  'propup-safe': defineAsyncComponent(() => import('@/modules/core/onboarding/pages/Propup.vue')),
}

const conditions = {
  'holding-back': (answers) => {
    return !answers['real-money'] || answers['real-money'] === 'no'
  },
  'improve': (answers) => {
    return answers['real-money'] && answers['real-money'] === 'yes'
  },
  notifications: async () => {
    if (!Capacitor.isNativePlatform()) return false

    const permissionStatus = await NotificationsService.checkPermissions()
    return permissionStatus === 'prompt'
  },
  teaser: async () => {
    return !SubService.isPro()
  },
  choose: async () => {
    return !SubService.isPro()
  },
  propup: async () => {
    return !SubService.isPro()
  },
  spin: async () => {
    return !SubService.isPro()
  }
}

// const progressComponents = {
//   bar: 'progress-bar',
//   steps: 'progress-steps',
// }

/**
 * Fetches onboarding questions and resolves their components and conditions.
 * @returns {Promise<*>}
 */
async function fetchQuestions() {
  const { data } = await StrapiService.getContentType('onboarding-questions', {
    populate: {
      options : {
        populate: ['icon']
      }
    },
    pagination: {
      pageSize: 100
    },
    sort: ['order:asc']
  })

  const cmsDomain = await ConfigService.getString('cms_url')

  // resolve option icons
  data.data.forEach(q => {
    q.attributes.options?.forEach(o => {
      if (!o.icon || !o.icon.data) {
        delete o.icon
      } else {
        o.icon = cmsDomain + o.icon.data.attributes?.url
      }
    })
  })

  return data.data
    .filter(q => q.attributes.type && components[q.attributes.type])
    .map(q => {
      return {
        ...q.attributes,
        id: q.id,
        component: markRaw(components[q.attributes.type]),
        condition: conditions[q.attributes.key]
      }
    })
}

/**
 * Fetches onboarding steps/pages and resolves their components and conditions.
 * @returns {Promise<*>}
 */
async function fetchPages() {
  const { data } = await StrapiService.getContentType('onboarding-steps', {
    populate: {
      component: {
        populate: ['icon']
      }
    },
    pagination: {
      pageSize: 100
    },
    sort: ['order:asc']
  })

  return data.data
    .filter(p => p.attributes.type && components[p.attributes.type])
    .map(p => {
      return {
        ...p.attributes,
        id: p.id,
        component: markRaw(components[p.attributes.type]),
        condition: conditions[p.attributes.key]
      }
    })
}

async function onboardingEnabled() {
  const { country_code: country  = 'LV' } = AppService.getAppInfo()
  const disabledTargets = await ConfigService.getJson('onboarding_disabled')

  // onboarding_disabled can be a boolean or an array with countries
  if (disabledTargets === true) {
    return false
  }

  return !country || !disabledTargets?.countries?.includes(country)
}

export default {
  fetchQuestions,
  fetchPages,
  onboardingEnabled
}
